/**
 * Save/load a calculator configuration to a local JSON file.
 *
 * The user owns the file; there is no server storage. The file carries
 * everything needed to rehydrate the calculator view exactly:
 *   - project_id: which project to select
 *   - params:     calculator params bag
 *   - answers:    questionnaire answers (for back-navigation)
 *   - name:       human-readable label shown in the header
 *
 * A tiny version field guards future format changes.
 */

export const CONFIG_FILE_VERSION = 1 as const;

export interface SavedConfig {
  version: typeof CONFIG_FILE_VERSION;
  name: string;
  project_id: string;
  saved_at: string;                         // ISO 8601 UTC
  answers: Record<string, unknown>;
  params: Record<string, unknown>;
}

export function buildSavedConfig(args: {
  name: string;
  project_id: string;
  params: Record<string, unknown>;
  answers: Record<string, unknown> | null;
}): SavedConfig {
  return {
    version: CONFIG_FILE_VERSION,
    name: args.name,
    project_id: args.project_id,
    saved_at: new Date().toISOString(),
    answers: args.answers ?? {},
    params: args.params,
  };
}

/** URL-safe kebab-case slug: letters+digits+hyphens only. */
export function slugify(s: string): string {
  return (s ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Canonical export filename: `<name>-<project>-cost-estimate.<ext>`.
 * Empty name falls back to "untitled". `projectLabel` is slugified —
 * pass the human-readable project name (e.g. "ESP RainMaker NG") when
 * it's available; otherwise the id ("rmng") is a reasonable fallback.
 */
export function buildFilename(
  name: string,
  projectLabel: string,
  ext: string,
): string {
  const n = slugify(name) || 'untitled';
  const p = slugify(projectLabel);
  return `${n}-${p}-cost-estimate.${ext}`;
}

/** Trigger a browser download for the given SavedConfig. */
export function downloadConfig(cfg: SavedConfig): void {
  const blob = new Blob([JSON.stringify(cfg, null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = buildFilename(cfg.name, cfg.project_id, 'json');
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/** Read a user-selected File and parse into SavedConfig. Throws on bad data. */
export async function readConfigFile(file: File): Promise<SavedConfig> {
  const text = await file.text();
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    throw new Error('File is not valid JSON.');
  }
  if (!data || typeof data !== 'object') {
    throw new Error('File does not contain a configuration object.');
  }
  const obj = data as Record<string, unknown>;
  if (obj.version !== CONFIG_FILE_VERSION) {
    throw new Error(
      `Unsupported config file version: ${String(obj.version)} (expected ${CONFIG_FILE_VERSION}).`,
    );
  }
  if (typeof obj.project_id !== 'string' || !obj.project_id) {
    throw new Error('Missing project_id.');
  }
  if (typeof obj.name !== 'string') {
    throw new Error('Missing name.');
  }
  if (!obj.params || typeof obj.params !== 'object') {
    throw new Error('Missing params object.');
  }
  const answers =
    obj.answers && typeof obj.answers === 'object' ? obj.answers : {};

  return {
    version: CONFIG_FILE_VERSION,
    name: obj.name,
    project_id: obj.project_id,
    saved_at: typeof obj.saved_at === 'string' ? obj.saved_at : new Date().toISOString(),
    answers: answers as Record<string, unknown>,
    params: obj.params as Record<string, unknown>,
  };
}
